/* eslint-disable import/no-cycle */
import { Dispatch } from 'react';
import { SessionState, Session, Invitation, Inviter, Bye } from 'sip.js';
// @ts-ignore
import Timer from 'easytimer.js';
import * as lib from '~/dependencies';
import * as actions from '~/store/actions';
import { RootState } from '~/store/config';
import {
  EnumSessionItems,
  IVideoCallCurrent,
} from '~/store/reducers/callCurrent';
import { EnumCallStatus } from '~/store/reducers/phone';
import { IExtra, ActionControl } from '~/interfaces';
import {
  extractSipCallerId,
  log,
  electron,
  formatCallerName,
  mediaStreamMonitor,
  getExtName,
  isExtension,
} from '~/helpers';
import * as actionTypes from './actionTypes';

/**
 * This function concat the queue name and number as a prefix for the contact name or number
 * @param queueName Queue name from sip headers
 * @param numberName Number name from sip headers
 * @param contactInfo Contact number or name
 * @returns QueueName-NumberName-ContactName
 */
function addContactDisplayNamePrefix(
  queueName: string | undefined,
  numberName: string | undefined,
  contactInfo: string | number | undefined
) {
  let prefix = null;

  if (queueName) prefix = `${queueName}-`;

  if (numberName) {
    prefix = prefix ? `${prefix}${numberName}-` : `${numberName}-`;
  }

  return prefix ? `${prefix}${contactInfo}` : contactInfo;
}

/**
 * Attempt to format the number to begin with a "+" e.164
 * @param phoneNumber
 * @param enforcePrefix Use for international number which does start with + or 00
 */
function addE164Prefix(
  phoneNumber: string | undefined,
  enforcePrefix: boolean = false
) {
  if (phoneNumber && enforcePrefix) return `+${phoneNumber}`;

  if (phoneNumber) {
    if (isExtension(phoneNumber)) return phoneNumber;

    if (phoneNumber?.startsWith('+')) return phoneNumber;

    if (phoneNumber?.startsWith('00')) return phoneNumber.replace(/^00/, '+');
  }

  return phoneNumber;
}

/**
 * Check the number with the local phonebook format(leading zeros).
 * @param phonebookNumber
 * @param remoteNumber
 */
const checkPhonebookFormat = (
  phonebookNumber: string,
  remoteNumber: string | undefined
) => {
  if (phonebookNumber && remoteNumber) {
    const leadingZeros = remoteNumber.replace(/^\+/, '00');

    if (phonebookNumber === remoteNumber) return true;

    if (phonebookNumber === leadingZeros) return true;
  }

  return false;
};

interface IContact {
  id: number;
  name: string;
  mobile: string;
  email: string;
  birthday: null | string; // Depending on how you want to handle null
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  landline: string;
  last_updated: string;
  letter: string;
  deleted: string; // Assuming "false" is a string
}

export const callsCurrent = (
  data: EnumSessionItems,
  action: 'add' | null = null
) => (dispatch: Dispatch<any>, getState: () => RootState) => {
  const currentCalls = getState().callCurrent.data;
  // const systemStatus = getState().system.status;

  // Gets the reducer clean when has not active calls
  // if (!currentCalls.length) {
  //   dispatch({ type: actionTypes.RESET_INTEGRATIONS_CONTACTS });
  // }

  // status need to be available or status different of add
  // because when you call with status busy is ok, but to receive you can't receive call on busy
  // if (systemStatus !== 'Busy' || action !== 'add') {
  dispatch({
    type: actionTypes.UPDATE_CALLS_CURRENT_LOADING,
    payload: {
      loading: true,
    },
  });

  const findInvite =
    data && data.length > 0 && data.find(e => e.invite === true);

  // to outgoing call if answered is possible hangup on jabra
  if (
    window.isElectron &&
    findInvite &&
    findInvite.answered &&
    findInvite.status !== 'CALL_FINISHED'
  ) {
    electron.outgoingCall({
      call: {
        id: String(findInvite.id),
        from: findInvite.from,
        status: findInvite.status,
      },
    });
  }

  dispatch({
    type: actionTypes.UPDATE_CALLS_CURRENT_FINISH,
    payload: {
      data: action ? [...currentCalls, data] : data,
      loading: false,
    },
  });
  // }
};

export const callActive = (session: Session | null = null) => (
  dispatch: Dispatch<any>
) => {
  dispatch({
    type: actionTypes.UPDATE_CALLS_CURRENT_ACTIVE,
    payload: {
      loading: false,
      session,
    },
  });
};

export const setVideoCallActivity = (isVideoCallActive: boolean) => (
  dispatch: Dispatch<any>
) => {
  dispatch({
    type: actionTypes.VIDEO_CALL_ACTIVE,
    payload: {
      data: isVideoCallActive,
    },
  });
};

export const updateCall = (
  status: ActionControl,
  id: string | null = null,
  value?: string | number | boolean
) => async (dispatch: Dispatch<any>, getState: () => RootState) => {
  let headerId = null;
  const { ua, extensions } = getState().phone;
  const { callCurrent } = getState();
  // Create a shallow copy of the .mute .answered .dtmf props
  const sessions: EnumSessionItems = lib._.clone(callCurrent.data);
  const foundIndex = callCurrent.data.findIndex(x => x.id === id);
  const foundIndexConf = callCurrent.data.findIndex(
    e => e.waitingToMergeConf === true
  );
  const options = {
    sessionDescriptionHandlerOptions: {
      constraints: {
        audio: true,
        video: false,
      },
    },
  };

  log.info(`Update call action: ${status} for session id: ${id}`);

  switch (status) {
    case 'accept':
      if (sessions[foundIndex]) {
        sessions[foundIndex].mute = false;
        sessions[foundIndex].answered = true;
        sessions[foundIndex].status = actionTypes.CALL_IN_PROGRESS;
        sessions[foundIndex].timerRinging.stop();
        (sessions[foundIndex].session as Invitation).accept(options);
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'accepted':
      if (sessions[foundIndex]) {
        // Update the caller name and number when the user picks up a call dialing the number 9.
        // if (sessions[foundIndex].session.assertedIdentity) {
        //   const callerName =
        //     sessions[foundIndex].session.assertedIdentity?.displayName;
        //   const callerNumber =
        //     sessions[foundIndex]?.session.assertedIdentity?.uri?.user;

        //   if (callerName && callerNumber) {
        //     sessions[foundIndex].contactName = callerName;
        //     sessions[foundIndex].numberFrom = callerNumber;
        //   }
        // }

        sessions[foundIndex].answered = true;
        sessions[foundIndex].status = actionTypes.CALL_IN_PROGRESS;
        sessions[foundIndex].timerRinging.stop();

        // NWA-865 - If there is another active call, then place this call as muted.
        // sessions.forEach(e => {
        //   if (
        //     e.id !== sessions[foundIndex].id &&
        //     !sessions[foundIndex].invite
        //   ) {
        //     dispatch(updateCall('mute', e.id));
        //   }
        // });

        // Bind the remote stream to the audio element.
        const remoteStream = new MediaStream();
        const sessionPeerConnection: RTCPeerConnection =
          // @ts-ignore
          // prettier-ignore
          sessions[foundIndex].session.sessionDescriptionHandler?.peerConnection;

        sessionPeerConnection.getReceivers().forEach(receiver => {
          if (receiver.track) {
            remoteStream.addTrack(receiver.track);
          }
        });
        dispatch(actions.remoteVideo(remoteStream));

        // Bind the local stream to the audio element.
        const localStream = new MediaStream();
        sessionPeerConnection.getSenders().forEach(sender => {
          if (sender.track) {
            localStream.addTrack(sender.track);
          }
        });

        mediaStreamMonitor.setMediaStreams({ remoteStream, localStream });

        dispatch(actions.addLocalVideo(localStream));

        // Always active the last call.
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'acceptOutgoing':
      if (sessions[foundIndex]) {
        sessions[foundIndex].invite = false;
        sessions[foundIndex].answered = true;
        sessions[foundIndex].status = actionTypes.CALL_IN_PROGRESS;
        sessions[foundIndex].timerRinging.stop();
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'finish':
      if (sessions[foundIndex]) {
        sessions[foundIndex].session.bye();
        sessions[foundIndex].finished = true;
        sessions[foundIndex].dtmf = false;
        sessions[foundIndex].status = actionTypes.CALL_FINISHED;
        sessions[foundIndex].timerDuring.stop();
        dispatch(callsCurrent(sessions));

        const remainingCallInProgress = sessions.find(e => e.id !== id);

        if (remainingCallInProgress) {
          dispatch(callActive(remainingCallInProgress.session));
        } else {
          dispatch(callActive());
        }
        dispatch(updateCall('clearCallsFinished', id));
      }
      break;
    case 'cancel':
      if (sessions[foundIndex]) {
        (sessions[foundIndex].session as Inviter).cancel();
        sessions[foundIndex].finished = true;
        sessions[foundIndex].dtmf = false;
        sessions[foundIndex].status = actionTypes.CALL_CANCELED;
        sessions[foundIndex].timerDuring.stop();
        sessions[foundIndex].timerRinging.stop();
        dispatch(callsCurrent(sessions));
        if (sessions.findIndex(e => e.id !== id) === -1) {
          dispatch(callActive());
        } else {
          dispatch(
            callActive(sessions[sessions.findIndex(e => e.id !== id)].session)
          );
        }
        dispatch(updateCall('clearCallsFinished', id));
      }
      break;
    case 'reject':
      if (sessions[foundIndex]) {
        const session = sessions[foundIndex].session as Invitation;
        if (session.state !== SessionState.Terminated) {
          session.reject({ statusCode: 603 });
          sessions[foundIndex].finished = true;
          sessions[foundIndex].dtmf = false;
          sessions[foundIndex].status = actionTypes.CALL_REJECTED;
          sessions[foundIndex].timerDuring.stop();
          sessions[foundIndex].timerRinging.stop();
        }

        // dispatch(callsCurrent(sessions));
        // setTimeout(() => {
        //   dispatch(updateCall('clearCallsFinished', id));
        // }, 3000);
      }
      break;
    case 'reinvite':
      if (sessions[foundIndex]) {
        // FIXME: Is reinvite working? it does not exist inside Session, Invitation or Inviter Classes
        // @ts-ignore
        sessions[foundIndex].session.reinvite();
        sessions[foundIndex].status = actionTypes.CALL_IN_PROGRESS;
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'p-asserted-identity':
      if (sessions[foundIndex]) {
        // @ts-ignore
        const { callerName, callerNumber } = extractSipCallerId(value);

        if (callerName && callerNumber) {
          // @ts-ignore
          const { queueName, numberName } = sessions[foundIndex];

          let reInviteCallerName;

          if (isExtension(callerNumber)) {
            reInviteCallerName = getExtName(extensions, callerNumber);
          } else {
            reInviteCallerName = callerName || callerNumber;
          }

          // @ts-ignore
          sessions[foundIndex].defaultDisplayName = addContactDisplayNamePrefix(
            queueName,
            numberName,
            reInviteCallerName
          );

          sessions[foundIndex].numberFrom = callerNumber;
          // @ts-ignore
          sessions[foundIndex].isReInvite = true;
        }
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'blindtransfer':
      if (sessions[foundIndex] && value) {
        const xOriginalCallId = sessions[
          foundIndex
          // @ts-ignore
        ].session.request.getHeader('X-Original-Callid');

        if (xOriginalCallId) {
          headerId = xOriginalCallId;
        } else {
          // @ts-ignore
          headerId = sessions[foundIndex].session.request.getHeader('Call-ID');
        }

        try {
          await sessions[foundIndex].session.refer(ua.makeURI(value), {
            requestOptions: {
              extraHeaders: [`X-Original-Callid: ${headerId}`],
            },
            onNotify: () => sessions[foundIndex].session.bye(),
          });

          sessions[foundIndex].finished = true;
          sessions[foundIndex].answered = false;
          sessions[foundIndex].status = actionTypes.CALL_TRANSFERED;
          sessions[foundIndex].timerDuring.stop();

          sessions[foundIndex].callActions = {
            ...sessions[foundIndex].callActions,
            transfer: false,
            conference: false,
            announcement: false,
            tags: false,
            notes: false,
          };

          dispatch(callsCurrent(sessions));
          dispatch(actions.numberContactToTransfer());
        } catch (err) {
          log.error(
            `NUACOM_LOG: Failure to perform the blind transfer. Reason: `,
            err
          );
          lib.ToastMessage(
            'error',
            'Failure to transfer, please try again.',
            7000
          );
        }
      }
      break;
    case 'attendedtransfer':
      if (sessions[foundIndex]) {
        const indexSessionToTransfer = sessions.findIndex(
          e => e.waitingTransfer == null
        );

        const xOriginalCallId = sessions[
          foundIndex
          // @ts-ignore
        ].session.request.getHeader('X-Original-Callid');

        if (xOriginalCallId) {
          headerId = xOriginalCallId;
        } else {
          // @ts-ignore
          headerId = sessions[foundIndex].session.request.getHeader('Call-ID');
        }

        await sessions[foundIndex].session.refer(
          sessions[indexSessionToTransfer].session,
          {
            requestOptions: {
              extraHeaders: [`X-Original-Callid: ${headerId}`],
            },
          }
        );

        sessions[foundIndex].finished = true;
        sessions[foundIndex].timerDuring.stop();
        sessions[foundIndex].status = actionTypes.CALL_TRANSFERED;
        dispatch(callActive());

        dispatch(callsCurrent(sessions.filter(e => e.id !== id)));
        dispatch(actions.numberContactToTransfer());
      }
      break;
    case 'mute':
      if (sessions[foundIndex]) {
        const sessionPeerConnection: RTCPeerConnection =
          // @ts-ignore
          // prettier-ignore
          sessions[foundIndex].session.sessionDescriptionHandler?.peerConnection;

        if (sessionPeerConnection?.getSenders) {
          sessionPeerConnection.getSenders().forEach(sender => {
            if (sender.track) {
              sender.track.enabled = false;
            }
          });

          sessions[foundIndex].mute = true;

          dispatch(callsCurrent(sessions));
        }

        // else {
        //   sessionPeerConnection?.getReceivers().forEach(sender => {
        //     if (sender.track) {
        //       sender.track.enabled = false;
        //     }
        //   });
        // }
      }
      break;
    case 'unmute':
      if (sessions[foundIndex]) {
        const sessionPeerConnection: RTCPeerConnection =
          // @ts-ignore
          // prettier-ignore
          sessions[foundIndex].session.sessionDescriptionHandler?.peerConnection;

        if (sessionPeerConnection.getSenders) {
          sessionPeerConnection.getSenders().forEach(sender => {
            if (sender.track) {
              sender.track.enabled = true;
            }
          });
        } else {
          sessionPeerConnection.getReceivers().forEach(sender => {
            if (sender.track) {
              sender.track.enabled = true;
            }
          });
        }
        sessions[foundIndex].mute = false;
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'dtmfAction':
      if (sessions[foundIndex]) {
        // @ts-ignore
        sessions[foundIndex].dtmf = value;
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'dtmf':
      if (sessions[foundIndex]) {
        // @ts-ignore
        sessions[foundIndex].dtmfValue = value;
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'transferOn':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          transfer: true,
          conference: false, // Hide conference component in case in active
          announcement: false,
        };
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'transferOff':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          transfer: false,
        };
        dispatch(callActive(sessions[foundIndex].session));
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'waitingTransfer':
      if (sessions[foundIndex]) {
        sessions[foundIndex].waitingTransfer = String(sessions[foundIndex].id);
        dispatch(callActive(sessions[foundIndex].session));
        // @ts-ignore
        dispatch(actions.numberContactToTransfer(value));
      }

      dispatch(callsCurrent(sessions));
      break;
    case 'hold':
      if (sessions[foundIndex] && !sessions[foundIndex].hold) {
        sessions[foundIndex].hold = true;
        sessions[foundIndex].status = actionTypes.CALL_HOLD;
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'unhold':
      if (sessions[foundIndex] && sessions[foundIndex].hold) {
        sessions[foundIndex].hold = false;
        sessions[foundIndex].status = actionTypes.CALL_IN_PROGRESS;
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          transfer: false,
          conference: false,
        };

        dispatch(callActive(sessions[foundIndex].session));

        sessions
          .filter(e => e.id !== sessions[foundIndex].id)
          .map(e => {
            e.hold = true;
            e.status = actionTypes.CALL_HOLD;
            return true;
          });

        dispatch(callsCurrent(sessions));
      }
      break;
    case 'remove':
      if (sessions[foundIndex]) {
        log.info(`Removing session: ${sessions[foundIndex].id}`);

        sessions[foundIndex].status = actionTypes.CALL_FINISHED;
        sessions[foundIndex].timerDuring.stop();
        sessions[foundIndex].timerRinging.stop();
        sessions[foundIndex].dtmf = false;

        // Only clean up the number to call if it was an outbound call.
        if (sessions[foundIndex].invite) {
          dispatch(actions.numberToTransfer());
          dispatch(actions.addTemporaryCountryCode(null, null));
        }

        dispatch(callsCurrent(sessions));

        const hasOtherCalls = sessions.some(e => e.id !== id);

        if (hasOtherCalls) {
          const remainingCallInProgress =
            sessions[sessions.findIndex(e => e.id !== id && e.answered)];
          // NWA-2497: Prevents problems triggered by self-calling and rejecting the same extension.
          if (remainingCallInProgress?.session) {
            dispatch(callActive(remainingCallInProgress.session));
          }
        } else {
          // If there are no remaining calls, clear the active call session.
          dispatch(callActive());
        }

        const remainingCalls = sessions
          .filter(s => s.id !== id)
          .map(c => ({
            ...c,
            callActions: {
              ...c.callActions,
              transfer: false,
            },
            waitingTransfer: false,
          }));

        log.info(`Remaining sessions count: ${remainingCalls.length}`);
        dispatch(callsCurrent(remainingCalls));

        /**
         * @deprecated It will be remove after v1.26.55 regression testing
         */
        // If a call is removed and there another call in HOLD state and waitingTransfer, means the removed call was start by a transfer action.
        // const waitingTransferIndex = sessions.findIndex(
        //   e => e.status === actionTypes.CALL_HOLD && e.waitingTransfer
        // );
        // // Re-enable the transfer button when call is hangup from the other side before transfer.
        // if (
        //   waitingTransferIndex !== -1 &&
        //   sessions[waitingTransferIndex].callActions
        // ) {
        //   sessions[waitingTransferIndex].callActions = {
        //     ...sessions[waitingTransferIndex].callActions,
        //     transfer: true,
        //   };
        // }
      }
      break;
    case 'clearCallsFinished':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          announcement: false,
          conference: false,
          transfer: false,
        };
        dispatch(callsCurrent(sessions.filter(e => e.id !== id)));
      }
      break;
    case 'readyToMerge':
      if (sessions[foundIndex]) {
        sessions[foundIndex].readyToMergeCall = Boolean(value);
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'conferenceOn':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          conference: true,
          transfer: false,
          announcement: false,
        };
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'conferenceOff':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions.conference = false;
        sessions[foundIndex].readyToMergeCall = false;
        sessions[foundIndex].waitingToMergeConf = false;
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'tagsOn':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          tags: true,
        };
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'tagsOff':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          tags: false,
        };
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'notesOn':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          notes: true,
        };
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'notesOff':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          notes: false,
        };
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'mergeCallConference':
      if (sessions[foundIndex] && sessions[1]) {
        sessions[foundIndex].status = actionTypes.CALL_CONFERENCE;
        sessions[1].status = actionTypes.CALL_CONFERENCE;
        sessions[foundIndex].callActions.conference = true;
        sessions[1].hold = false;
        sessions[1].waitingTransfer = null;
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'splitCallConference':
      if (foundIndexConf) {
        sessions.map(e => {
          if (e.id === sessions[foundIndexConf].id) {
            sessions[foundIndexConf].status = actionTypes.CALL_IN_PROGRESS;
            sessions[foundIndex].callActions.conference = false;
          }
          return true;
        });

        dispatch(callsCurrent(sessions));
        dispatch(callActive(sessions[foundIndexConf].session));
      }
      break;
    case 'announcementOn':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions = {
          ...sessions[foundIndex].callActions,
          announcement: true,
          conference: false,
          transfer: false,
        };
        dispatch(callsCurrent(sessions));
      }
      break;
    case 'announcementOff':
      if (sessions[foundIndex]) {
        sessions[foundIndex].callActions.announcement = false;
        dispatch(callsCurrent(sessions));
      }
      break;
    default:
      break;
  }
};

export const verifyIfReadyToMerge = (id: string, action: boolean = true) => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { callCurrent } = getState();
  const indexCall = callCurrent.data.findIndex(e => e.waitingTransfer !== null);

  if (indexCall !== -1) {
    if (callCurrent.data[indexCall].waitingTransfer) {
      dispatch(
        updateCall(
          'readyToMerge',
          String(callCurrent.data[indexCall].waitingTransfer),
          action
        )
      );
    }
  }
};

export const resetCallsCurrent = () => (dispatch: Dispatch<any>) => {
  dispatch({ type: actionTypes.RESET_CALLS_CURRENT });
};

export const addNewSession = (session: Session, extra?: IExtra) => (
  dispatch: Dispatch<any>,
  getState: () => RootState
) => {
  const { ua } = getState().phone;
  const { status } = getState().system;
  const {
    dataAll: allContacts,
    dataPersonal: personalContacts,
  } = getState().contact;
  const { data: activeCalls } = getState().callCurrent;
  const { extensions } = getState().phone;

  // e.g: Inboard: TestQueue-QATestNumber-012118377 | Outboard: 00353833039999@webrtc-gw.nuacom.net
  const friendlyName = session?.remoteIdentity?.friendlyName.toString();
  // e.g: "00353833039999"
  const remoteId = session?.remoteIdentity?.uri?.user?.toString();
  // @ts-ignore
  const xCallId = session.request.getHeader('Call-ID');
  // @ts-ignore For incoming calls only
  const xQueueName = session.request?.getHeader('X-Nuacom-QueueName');
  // @ts-ignore For incoming calls only
  const xNumberName = session.request?.getHeader('X-Nuacom-NumberName');
  // @ts-ignore For incoming calls only
  const xContactID: number = Number(session.request.getHeader('X-ContactID'));
  // @ts-ignore
  const xNuacomTransfferedBy = session.request?.getHeader(
    'X-Nuacom-Transferred-By'
  );
  // @ts-ignore For incoming calls only. It does not contains + or 00 on start
  const xInterIncomingSrcNumber = session.request?.getHeader(
    'X-Nuacom-SrcInternationalNumber'
  );

  // @ts-ignore
  const xOriginalCallId = session.request?.getHeader('X-Original-CallID');

  const isInvite = extra?.invite;
  let contactName = formatCallerName(extra?.name || friendlyName);
  let callId = session.id;
  let contactId = extra?.contactId;
  let localContactName: string | null = null;

  const phoneNumber: string =
    addE164Prefix(xInterIncomingSrcNumber, true) || addE164Prefix(remoteId)!;

  if (!isExtension(phoneNumber)) {
    // If not extension, then fetch integrated contacts and try to pick contact name from phonebook
    callId = xCallId.split('@')[0].replace('"', '');

    dispatch(
      actions.getIntegratedContacts(phoneNumber, callId, xOriginalCallId)
    );

    // FIXME: Filter per admin here in order to solve issues NWA-1296
    // Check if the callee is on the local contact list and try to extract its name. Contains all contacts including personals.
    const contacts = [
      ...(allContacts || []).flatMap((contact: any) => contact.data),
      ...(personalContacts || []).flatMap((contact: any) => contact.data),
    ];

    // Filter local phonebook contacts based on session details
    const sessionLocalContacts: IContact[] = contacts.filter(
      (e: IContact) =>
        e.id === xContactID ||
        checkPhonebookFormat(e.mobile, phoneNumber) ||
        checkPhonebookFormat(e.landline, phoneNumber) ||
        checkPhonebookFormat(e.landline, remoteId) ||
        checkPhonebookFormat(e.mobile, remoteId)
    );

    // Set the first found phonebook contact as displayName
    if (sessionLocalContacts.length > 0) {
      contactId = sessionLocalContacts[0].id;
      contactName = sessionLocalContacts[0].name;
      localContactName = sessionLocalContacts[0].name;
    }
  }

  // Get announcements, doing this here to void duplicate requests.
  dispatch(actions.fetchAnnouncements());

  // Change the Electron icon badge to in-use.
  electron.changePhoneStatus('Available', true);

  // Session Delegate
  session.delegate = {
    onInvite: request => {
      // @ts-ignore
      const newPaiToken = request.getHeader('P-Asserted-Identity');

      if (newPaiToken) {
        dispatch(
          actions.updateCall('p-asserted-identity', session.id, newPaiToken)
        );
      }
    },
    onBye: (bye: Bye) => {
      bye.accept();
      setTimeout(() => {
        dispatch(actions.PlayRing('endTone', true));
      }, 1000);
    },
  };

  // Handle session state changes.
  session.stateChange.addListener(state => {
    switch (state) {
      case SessionState.Initial:
        break;
      case SessionState.Establishing:
        break;
      case SessionState.Established:
        log.info(`SessionState: Call established.`);
        // @ts-ignore
        if (session?.incomingInviteRequest) {
          // Tell to Electron that this call is answered.
          electron.callAnswered(session.id, 'INCOMING');
        } else {
          electron.callAnswered(session.id, 'OUTGOING');
        }
        dispatch(actions.PlayRing('ringTone', false));
        dispatch(actions.PlayRing('outgoingRing', false));
        dispatch(actions.verifyIfReadyToMerge(session.id));
        dispatch(updateCall('accepted', session.id));
        break;
      case SessionState.Terminating:
        break;
      case SessionState.Terminated:
        log.info(`SessionState: Call terminated.`);
        // Tell to Electron that this call was terminated.
        electron.callFinished(session.id);
        // If the app status is Busy then do unregistering.
        if (status === actionTypes.STATUS.BUSY && !activeCalls.length) {
          ua.unregister();
          electron.changePhoneStatus('Busy', false);
        } else if (!activeCalls.length) {
          electron.changePhoneStatus('Available', false);
        }

        try {
          const terminatedCall = getState().callCurrent.data.find(
            (c: any) => c.id === session.id
          );

          if (terminatedCall?.answered) {
            const xQueueId =
              // @ts-ignore
              session.request.getHeader('X-Nuacom-QueueID') || null;
            dispatch(
              actions.checkAndStoreMandatoryCallTagging({
                callId: isInvite ? xCallId : xOriginalCallId,
                contactName: contactName || '',
                numberFrom: xNuacomTransfferedBy || phoneNumber,
                direction: isInvite ? 'outbound' : 'inbound',
                directionNumber: isInvite ? 3 : 1,
                queueId: xQueueId,
                outboundCli: getState().auth?.outbound_cli,
              })
            );
          }
        } catch (err) {
          log.error('Failed to prepare mandatory call tagging arguments:', err);
        }

        dispatch(actions.updateCall('remove', session.id));
        dispatch(actions.PlayRing('ringTone', false));
        dispatch(actions.PlayRing('outgoingRing', false));
        dispatch(actions.numberToTransfer());
        dispatch(actions.callInProgress(false));
        dispatch(actions.callStatus(EnumCallStatus.CALL_FINISHED));
        break;
      default:
        throw new Error('NUACOM_LOG: Unknown session state.');
    }
  });

  let defaultDisplayName;

  if (isExtension(phoneNumber!)) {
    const extensionName = getExtName(extensions, phoneNumber!);
    defaultDisplayName = extensionName || phoneNumber;
  } else {
    defaultDisplayName = localContactName || phoneNumber;
  }

  if (isExtension(xNuacomTransfferedBy!)) {
    const transfferedByExtensionName = getExtName(
      extensions,
      xNuacomTransfferedBy
    );
    defaultDisplayName = transfferedByExtensionName || xNuacomTransfferedBy;
  } else if (xNuacomTransfferedBy && !xContactID) {
    defaultDisplayName = xNuacomTransfferedBy;
  } else if (xContactID && localContactName) {
    defaultDisplayName = localContactName;
  }

  defaultDisplayName = addContactDisplayNamePrefix(
    xQueueName,
    xNumberName,
    defaultDisplayName
  );

  if (session) {
    const newSession = {
      id: session.id,
      from: contactName,
      numberFrom: xNuacomTransfferedBy || phoneNumber,
      defaultDisplayName,
      localContactName,
      isReInvite: false,
      contactId: contactId === -1 || !contactId ? null : contactId,
      contactName,
      queueName: xQueueName,
      numberName: xNumberName,
      session,
      mute: false,
      timerRinging: new Timer(),
      timerDuring: new Timer(),
      hold: false,
      invite: isInvite ?? false,
      directionNumber: isInvite ? 3 : 1,
      answered: false,
      finished: false,
      transfer: false,
      dialpad: false,
      dtmf: false,
      dtmfValue: '',
      waitingToMergeConf: !!(extra && extra.waitingToMergeConf),
      waitingTransfer: null,
      readyToMergeCall: false,
      status: isInvite ? actionTypes.CALL_OUTGOING : actionTypes.CALL_INCOMING,
      callActions: {
        transfer: false,
        conference: false,
        announcement: false,
        tags: false,
        notes: false,
      },
      /**
       * Define which contact name or number must be showed, from CRM integrations, extensions or phonebook.
       * @param contactNameOverride The idea is to OVERRIDE the defaultDisplayName which is initially defined when the call started.
       * The contactNameOverride be used to change the call display name anytime after the call already started.
       * We are using it to replace the sip header contact name with the CRM integration contact name
       * @returns ContactName, concat with queue name and number name if exists
       */
      getDisplayName(displayNameOverride: string | undefined) {
        if (!displayNameOverride) {
          return this.defaultDisplayName;
        }

        if (this.isReInvite) {
          return this.defaultDisplayName;
        }

        return addContactDisplayNamePrefix(
          this.queueName,
          this.numberName,
          displayNameOverride
        );
      },
      /**
       * Return a string callId withot before @ only
       */
      getFormattedCallId() {
        // @ts-ignore
        if (this.session.request.getHeader('Call-ID')) {
          // @ts-ignore
          const headerCallId = this.session.request.getHeader('Call-ID');

          return headerCallId.split('@')[0].replace('"', '');
        }

        return undefined;
      },
    };

    // @ts-ignore
    dispatch(callsCurrent(newSession, 'add'));
  }

  // Notify electron for incoming call.
  if (window.isElectron && !isInvite) {
    const numberOfActiveCalls = activeCalls.length ?? 0;
    // FIXME: Re-think this logic in order to remove the @ts-ignore because Data can be both array or plain object
    const incomingCallInfo = {
      call: {
        id: session.id,
        from: String(defaultDisplayName),
      },
      activeCalls: numberOfActiveCalls,
    };
    electron.incomingCall(incomingCallInfo);
  }
};

export const startNewVideoCall = (video: IVideoCallCurrent) => (
  dispatch: Dispatch<any>
) => {
  dispatch({ type: actionTypes.VIDEO_NEW_CALL, payload: { data: video } });
};

export const updateVideoCall = (video: IVideoCallCurrent | null) => (
  dispatch: Dispatch<any>
) => {
  dispatch({ type: actionTypes.UPDATE_VIDEO_CALL, payload: { data: video } });
};
